/**
 * Chat Sidebar Component
 * Premium chat history UI with custom delete modal
 */
import React, { useState, useCallback } from 'react';
import { useSophieChat } from '../../context/SophieChatContext';
import { Plus, MessageSquare, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Helper to format relative time
const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};

export const ChatSidebar: React.FC = () => {
  const { 
    chats, 
    currentChatId, 
    isLoadingChats, 
    startNewChat, 
    loadChat, 
    deleteChat 
  } = useSophieChat();
  
  // Delete modal state
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle chat selection - instant, no loading animation
  const handleSelectChat = useCallback((chatId: string) => {
    if (chatId === currentChatId) return;
    loadChat(chatId);
  }, [currentChatId, loadChat]);

  // Open delete confirmation modal
  const handleDeleteClick = (e: React.MouseEvent, chatId: string, title: string) => {
    e.stopPropagation();
    setDeleteTarget({ id: chatId, title });
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    
    setIsDeleting(true);
    try {
      await deleteChat(deleteTarget.id);
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <>
      <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 w-64">
        {/* Header */}
        <div className="p-3 border-b border-gray-200 dark:border-gray-800">
          <Button 
            onClick={startNewChat}
            className="w-full justify-start gap-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 shadow-sm"
            variant="outline"
          >
            <Plus size={16} />
            New Chat
          </Button>
        </div>

        {/* Chat List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {isLoadingChats ? (
            <div className="flex items-center justify-center py-8 text-gray-400">
              <Loader2 className="animate-spin" size={20} />
            </div>
          ) : chats.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              <MessageSquare className="mx-auto mb-2 opacity-50" size={24} />
              <p>No conversations yet</p>
            </div>
          ) : (
            chats.map((chat) => {
              const isActive = currentChatId === chat.id;
              
              return (
                <div
                  key={chat.id}
                  onClick={() => handleSelectChat(chat.id)}
                  className={`
                    group relative flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-all duration-150
                    ${isActive 
                      ? 'bg-white dark:bg-gray-800 shadow-sm border border-indigo-200 dark:border-indigo-700' 
                      : 'hover:bg-white/70 dark:hover:bg-gray-800/70'
                    }
                  `}
                >
                  {/* Icon */}
                  <MessageSquare size={14} className={`flex-shrink-0 ${isActive ? 'text-indigo-500' : 'text-gray-400'}`} />
                  
                  {/* Content - with proper padding for delete button */}
                  <div className="flex-1 min-w-0 pr-6">
                    <p className={`text-sm font-medium truncate ${isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-900 dark:text-white'}`}>
                      {chat.title}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatRelativeTime(chat.updatedAt)}
                    </p>
                  </div>

                  {/* Delete Button - positioned absolutely with proper spacing */}
                  <button
                    onClick={(e) => handleDeleteClick(e, chat.id, chat.title)}
                    className={`
                      absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded-md transition-all duration-150
                      opacity-0 group-hover:opacity-100 focus:opacity-100
                      hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500
                    `}
                    title="Delete conversation"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-2 border-t border-gray-200 dark:border-gray-800 text-center">
          <p className="text-[10px] text-gray-400">
            {chats.length} conversation{chats.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Custom Delete Confirmation Modal */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <AlertDialogTitle>Delete Conversation</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="pt-2">
              Are you sure you want to delete <span className="font-medium text-gray-900 dark:text-white">"{deleteTarget?.title}"</span>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
